<template>
  <section id="hero" class="hero-section">
    <div class="hero-overlay"></div>
    <div class="container hero-content">
      <p class="hero-subtitle">OPPORTUNITIES</p>
      <h1 class="hero-title">Funding, training & scholarship opportunities</h1>
    </div>
  </section>

  <main class="main-content-wrapper">
    <div class="container" style="padding-top: 130px;">
      <div
        v-for="(items, category) in orderedFundings"
        :key="category"
        class="opportunity-group"
      >
        <h3 class="opportunity-title mb-4">{{ displayCategory(category) }}</h3>
        <!-- If one item in this category is expanded, render it full-width above the grid -->
        <div v-if="expandedItemByCategory(category)" class="expanded-wrapper">
          <article class="opportunity-card expanded-card">
            <h4 class="card-title">{{ expandedItemByCategory(category).title }}</h4>
            <p class="card-date">{{ formatDate(expandedItemByCategory(category).created_at) }}</p>

            <div class="card-full-text">
              <p v-html="expandedItemByCategory(category).content"></p>

              <p v-if="expandedItemByCategory(category).link" class="mt-3">
                <strong>Visit:</strong>
                <a :href="expandedItemByCategory(category).link" target="_blank" class="text-primary">
                  {{ expandedItemByCategory(category).link }}
                </a>
              </p>
            </div>

            <div class="expanded-actions">
              <button class="btn-ghost" @click="toggleExpand(expandedItemByCategory(category).id)">
                View less
                <svg xmlns="http://www.w3.org/2000/svg" width="6" height="10" viewBox="0 0 6 10" fill="none"><path d="M1.5 2L4.5 5L1.5 8" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </button>
            </div>
          </article>
        </div>

        <!-- Grid of remaining items (if expanded, excludes that item) -->
        <div class="opportunity-grid">
          <article
            v-for="item in visibleItems(items, category)"
            :key="item.id"
            class="opportunity-card"
          >
            <h4 class="card-title">{{ item.title }}</h4>
            <p class="card-date">{{ formatDate(item.created_at) }}</p>
            <p class="card-text">{{ excerpt(item.content, 140) }}</p>

            <div class="card-actions">
              <button class="view-more-btn" @click="toggleExpand(item.id)">
                Read more
                <svg xmlns="http://www.w3.org/2000/svg" width="6" height="10" viewBox="0 0 6 10" fill="none"><path d="M1.5 2L4.5 5L1.5 8" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </button>
            </div>
          </article>
        </div>

        <!-- Show more/less button if > 4 -->
        <div v-if="items.length > 5" class="text-end">
          <button class="show-more-btn" @click="toggleShowMore(category)">
            {{ expandedCategories.includes(category) ? 'Show less' : 'Show more' }}
          </button>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import FundingService from '@/services/funding/FundingService'
import AnalyticsService from '@/services/analytics/AnalyticsService'

export default {
  name: 'Funding',
  data() {
    return {
      fundings: {},
      expandedCategories: [],
      expandedItems: []
    }
  },
  async mounted() {
    const result = await FundingService.getPublic()
    this.fundings = result?.Data?.data || result || {}
  },
  computed: {
    orderedFundings() {
      const priority = [
        'funding',
        'training',
        'scholarship'
      ]

      // Convert object → array with metadata
      const entries = Object.entries(this.fundings).map(([category, items]) => {
        const normalized = category.toLowerCase()

        let orderIndex = priority.length + 1
        priority.forEach((key, index) => {
          if (normalized.includes(key)) {
            orderIndex = index
          }
        })

        return {
          category,
          items,
          orderIndex
        }
      })

      // Sort by priority
      entries.sort((a, b) => a.orderIndex - b.orderIndex)

      // Convert back to ordered object
      const ordered = {}
      entries.forEach(e => {
        ordered[e.category] = e.items
      })

      return ordered
    }
  },
  methods: {
    displayCategory(category) {
      if (!category) return ''

      const key = category.toLowerCase()

      if (key.includes('funding')) {
        return 'Funding opportunities'
      }

      if (key.includes('training')) {
        return 'Training opportunities'
      }

      if (key.includes('scholarship')) {
        return 'Scholarship opportunities'
      }

      // fallback – show original if unknown
      return category
    },

    truncate(text, length) {
      if (!text) return ''
      return text.length > length ? text.substring(0, length) + '...' : text
    },

    // Plain-text excerpt for the tiles. Drops <style>/<script>/<xml> blocks and
    // comments (e.g. Word "Normal 0 false false EN-ZA X-NONE" paste junk) before
    // removing tags, then decodes entities and collapses whitespace.
    excerpt(html, limit) {
      if (!html) return ''

      const el = document.createElement('div')
      el.innerHTML = String(html)
        .replace(/<!--[\s\S]*?-->/g, ' ')
        .replace(/<(style|script|xml)\b[\s\S]*?<\/\1>/gi, ' ')
        .replace(/<\/(p|div|li|h[1-6]|br)>|<br\s*\/?>/gi, ' ')
        .replace(/<[^>]+>/g, '')

      const text = (el.textContent || '').replace(/\s+/g, ' ').trim()
      return text.length > limit ? text.substring(0, limit).trimEnd() + '...' : text
    },

    visibleItems(items, category) {
      const expandedId = this.expandedItemByCategory(category)?.id
      const filtered = items.filter(it => it.id !== expandedId)
      return this.expandedCategories.includes(category) ? filtered : filtered.slice(0, 5)
    },

    expandedItemByCategory(category) {
      const items = this.fundings[category] || []
      const foundId = this.expandedItems.find(id => items.some(it => it.id === id))
      return foundId ? items.find(it => it.id === foundId) : null
    },

    toggleShowMore(category) {
      const i = this.expandedCategories.indexOf(category)
      if (i === -1) this.expandedCategories.push(category)
      else this.expandedCategories.splice(i, 1)
    },

    toggleExpand(id) {
      // find the category this item belongs to
      let categoryFound = null
      for (const [category, items] of Object.entries(this.fundings)) {
        if (items.some(it => it.id === id)) {
          categoryFound = category
          break
        }
      }
      if (!categoryFound) return

      const isAlreadyExpanded = this.expandedItems.includes(id)

      this.expandedItems = this.expandedItems.filter(
        eid => !(this.fundings[categoryFound] || []).some(it => it.id === eid)
      )

      if (!isAlreadyExpanded) {
        this.expandedItems.push(id)
        const item = (this.fundings[categoryFound] || []).find(it => it.id === id)
        AnalyticsService.trackEvent('view_funding_opportunity', {
          funding_id: id,
          title: item?.title
        })
      }
    },

    formatDate(date) {
      if (!date) return ''
      const d = new Date(date)
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    }
  }
}
</script>

<style scoped>
/* HERO SECTION */
.hero-section {
  position: relative;
  background-image: url('/public/33ad975811d706b6ec2e9ccd50aca96d7ac62133.png');
  background-size: cover;
  background-position: center;
  height: 247px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: var(--color-white);
}
.hero-overlay {
  position: absolute;
  width: 100%;
  height: 100%;
  background-color: var(--color-primary);
  opacity: 0.1;
}
.hero-content { position: relative; z-index: 1; }
.hero-subtitle { margin-bottom: 8px; font-size: var(--font-size-lg); font-weight: var(--font-weight-bold); }
.hero-title { font-size: var(--font-size-5xl); font-weight: var(--font-weight-bold); line-height: var(--line-height-tight); }

/* MAIN */
.main-content-wrapper { background-color: var(--color-light-gray); padding-bottom: 80px; }
.opportunity-group { margin-bottom: 60px; }
.opportunity-title { font-size: var(--font-size-2xl); font-weight: var(--font-weight-bold); border-bottom: 1px solid #ddd; padding-bottom: 10px; margin-bottom: 20px; }

/* EXPANDED wrapper (full width card) */
.expanded-wrapper { margin-bottom: 20px; }
.expanded-card {
  background: white;
  padding: 26px;
  border-radius: 12px;
  box-shadow: 0 4px 25px rgba(0,0,0,0.06);
}

/* grid for small cards */
.opportunity-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 20px;
}

/* card (small) */
.opportunity-card {
  background: white;
  padding: 18px;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 220px;
}
.card-title { font-size: var(--font-size-xl); font-weight: var(--font-weight-bold); color: #004d50; margin-bottom: 6px; }
.card-date { font-size: var(--font-size-xs); color: #6c757d; margin-bottom: 8px; }
.card-text { font-size: var(--font-size-sm); color: #444; flex-grow: 1; margin-bottom: 12px; }
.card-full-text p { margin: 0 0 12px 0; line-height: var(--line-height-relaxed); color: #333; }

/* actions */
.card-actions { margin-top: auto; }
.view-more-btn {
  background-color: #00838f;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 14px;
  font-weight: var(--font-weight-semibold);
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}
.btn-ghost {
  background: #6c757d;
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
}
.show-more-btn {
  background-color: #f59e0b;
  color: white;
  border: none;
  padding: 8px 22px;
  border-radius: 6px;
  font-weight: var(--font-weight-semibold);
  margin-top: 20px;
  cursor: pointer;
}

/* responsive tweaks */
@media (max-width: 768px) {
  .hero-title { font-size: var(--font-size-3xl); }
  .opportunity-grid { grid-template-columns: 1fr; }
}
</style>
